import { ComponentProps, forwardRef } from 'react';
import ArrowIcon from '@/common/icons/ArrowIcon';
import { cn } from '@/common/utils';
import Button from '../Button';
import { Slot } from '../Slot';
import {
  type DashboardSidebarContextProps,
  dashboardSidebarCtx,
  useDashboardSidebarCtx,
} from './context';
import { ATSSwitcherIcon } from './SidebarLogos/ats-switcher';
import { Brand as BrandLogo, BrandSmall } from './SidebarLogos/brand';
import { DeallsIcon } from './SidebarLogos/dealls';
import { KantorkuSwitcherIcon } from './SidebarLogos/kantorku-switcher';
import * as Routes from './SidebarRoutes/components';

type Div = JSX.IntrinsicElements['div'];
type Anchor = JSX.IntrinsicElements['a'];
type Nav = JSX.IntrinsicElements['nav'];

const Root = ({
  className,
  value,
  ...props
}: Div & {
  value: DashboardSidebarContextProps;
}) => {
  return (
    <dashboardSidebarCtx.Provider value={value}>
      <div className={cn('relative', className)} {...props}></div>
    </dashboardSidebarCtx.Provider>
  );
};

const Backdrop = ({ className, children: _, ...props }: Div) => {
  const { isMobileSize, sidebarExpanded } = useDashboardSidebarCtx();
  const mobileSidebarExpanded = isMobileSize && sidebarExpanded;
  return (
    <div
      className={cn(
        'fixed inset-0 z-[30] block bg-neutral-1000/50 998:hidden',
        mobileSidebarExpanded ? '' : 'hidden',
      )}
      {...props}
    />
  );
};

const Trigger = ({
  className,
  children,
  ...props
}: Omit<ComponentProps<typeof Button>, 'variant'>) => {
  const { sidebarExpanded } = useDashboardSidebarCtx();
  return (
    <Button
      className={cn(
        'sidebar-toggle transition-all duration-500 rounded-full bg-violet-500 text-white hover:bg-violet-500 hover:text-white active:bg-violet-500 active:text-white',
        'hidden 998:flex 998:z-[99] absolute mt-1 right-0 -translate-x-[200px] px-0 998:translate-x-1/2',
        sidebarExpanded
          ? '!size-6 top-[54px] z-[99]'
          : '!size-[18px] top-[56px]',
        className,
      )}
      type="primary"
      {...props}
    >
      {children || (
        <>
          {' '}
          {sidebarExpanded ? (
            <ArrowIcon.Left className="transition-all size-[12px]" />
          ) : (
            <ArrowIcon.Right className="transition-all size-[14px]" />
          )}
        </>
      )}
    </Button>
  );
};

const Container = forwardRef<HTMLElement, Nav>(
  ({ className, ...props }, ref) => {
    const { sidebarExpanded, isMobileSize } = useDashboardSidebarCtx();
    return (
      <nav
        aria-label="Sidebar"
        ref={ref}
        className={cn(
          'z-50 flex min-h-full flex-col justify-between overflow-y-auto overflow-x-hidden themed-scroll-y bg-[#F7F5FA] px-3 transition-all duration-500 pb-6 998:pb-9',
          '998:relative',
          '[scrollbar-gutter:stable]',
          sidebarExpanded
            ? 'w-[144px] 998:w-[150px] pt-6 998:pt-4 998:px-3'
            : 'w-[54px] 998:w-[54px] 998:pt-3.5 shadow-inner',
          isMobileSize && !sidebarExpanded && 'translate-x-[-150px]',
          isMobileSize && 'fixed left-0 top-0 bottom-0 z-[40]',
        )}
        {...props}
      />
    );
  },
);

const PoweredBy = ({ className, children: _, ...props }: Div) => {
  const { isMobileSize, sidebarExpanded } = useDashboardSidebarCtx();

  if (!sidebarExpanded && !isMobileSize) {
    return null;
  }

  return (
    <div
      aria-hidden
      className={cn(
        'flex items-center justify-center gap-1 !text-sh9 text-neutral-700',
        className,
      )}
      {...props}
    >
      Powered by
      <DeallsIcon />
    </div>
  );
};

const Content = ({ className, ...props }: Div) => {
  const { sidebarExpanded } = useDashboardSidebarCtx();

  return (
    <div
      className={cn(
        'flex flex-col gap-4 items-start transition-[padding] duration-300',
        !sidebarExpanded && 'pl-1',
      )}
      {...props}
    ></div>
  );
};

const SwitchersContent = ({ className, ...props }: Div) => {
  const { sidebarExpanded } = useDashboardSidebarCtx();

  return (
    <div
      className={cn(
        'flex items-center justify-between gap-1 transition-opacity duration-500',
        !sidebarExpanded && 'invisible opacity-0',
      )}
      {...props}
    ></div>
  );
};

const SwitcherItem = ({
  className,
  children,
  asChild,
  variant,
  ...props
}: Anchor & { asChild?: boolean; variant: 'primary' | 'outlined' }) => {
  const Comp = asChild ? Slot : 'a';
  return (
    <Comp
      className={cn(
        'rounded-[46px] flex h-6 w-[61px] gap-1 px-2.5 !text-sh10 font-bold items-center justify-center',
        'border border-violet-500',
        variant === 'primary' && 'bg-violet-500 text-white hover:text-white',
        variant === 'outlined' &&
          'bg-transparent text-neutral-800 hover:text-neutral-800',
        className,
      )}
      {...props}
    >
      {children}
    </Comp>
  );
};

const SwitcherItemATS = () => {
  return (
    <>
      <ATSSwitcherIcon className="h-[9px] shrink-0" />
      ATS
    </>
  );
};

const SwitcherItemKantorku = () => {
  return (
    <>
      <KantorkuSwitcherIcon className="h-[9px] shrink-0" />
      HRIS
    </>
  );
};

const Brand = ({ className, ...props }: Div) => {
  const { sidebarExpanded } = useDashboardSidebarCtx();

  return (
    <div
      aria-label="Dealls Logo"
      className={cn(
        'relative flex items-start px-1 998:px-1',

        className,
      )}
      {...props}
    >
      <BrandLogo
        className={cn(
          'h-auto mt-0.5 -ml-0.5 transition duration-500',
          sidebarExpanded ? '-ml-0.5' : 'opacity-0 -ml-1 -translate-x-2',
        )}
      />
      <BrandSmall
        className={cn(
          'h-[28px] mb-3 -mr-1 absolute -left-0.5 transition duration-500',
          sidebarExpanded ? 'opacity-0 scale-75' : 'opacity-100 mr-0',
        )}
      />
    </div>
  );
};

const DashboardSidebar = {
  Root,
  Backdrop,
  Trigger,
  Container,
  PoweredBy,
  Content,
  SwitchersContent,
  SwitcherItem,
  SwitcherItemATS,
  SwitcherItemKantorku,
  Brand,

  ...Routes,
};

export { DashboardSidebar };

export type { DashboardSidebarContextProps };
